import { useCallback, useEffect, useRef, useState } from 'react'
import { useLiveQuery } from '@tanstack/react-db'
import { useRouter, useSearch } from '@tanstack/react-router'

import type { LedgerServerQuery } from '@/components/ui/LedgerTable'
import { useAuth } from '@/lib/auth'
import { useDb } from '@/lib/dbContext'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import {
  followUpSearchSortKeys,
  remainingSchedule,
  type FollowUp,
  type FollowUpCategory,
  type FollowUpSearchSortKey,
  type FollowUpServiceType,
  type FollowUpsSearchFilters,
  type ScheduleStep,
} from '@/db/domain/followUps'
import type { Customer } from '@/lib/schemas/db'
import type { CustomerNote } from '@/db/domain/models'
import { buildCustomerNoteFeed } from '@/screens/serviceFeed'
import { toZaDateKey } from '@/lib/datetime'
import {
  FollowUpsScreen,
  type FollowUpLinkedNote,
  type FollowUpRow,
  type FollowUpsServerScope,
} from '@/screens/FollowUpsScreen'

const serviceLabels: Record<FollowUpServiceType, string> = {
  callout: 'Callout',
  remote: 'Remote session',
  sitdown: 'Sit-down session',
  job: 'Job',
}

function isSortKey(key: string): key is FollowUpSearchSortKey {
  return (followUpSearchSortKeys as readonly string[]).includes(key)
}

/** Column keys whose text inputs should debounce before hitting the server. */
const debouncedFilterKeys = ['customer', 'title']

function textFiltersChanged(
  a: Record<string, string>,
  b: Record<string, string>,
) {
  return debouncedFilterKeys.some((key) => (a[key] ?? '') !== (b[key] ?? ''))
}

/** A filter for a name we can't resolve must match nothing, not everything. */
const NO_MATCH_ID = '__no-match__'

/**
 * How long a mutation gets to sync before the membership re-query. The
 * follow-up commands write optimistically and persist in the background, so
 * an immediate search would read (and re-upsert) pre-write server state.
 */
const SERVER_REFRESH_DELAY_MS = 1200

export function FollowUpsScreenContainer() {
  const db = useDb()
  const router = useRouter()
  const { user } = useAuth()
  const [photoNoteError, setPhotoNoteError] = useState<string | null>(null)
  const search = useSearch({ strict: false }) as {
    tab?: 'upcoming' | 'archive'
    new?: boolean
  }
  const activeTab =
    search.tab === 'upcoming' || search.tab === 'archive' ? search.tab : 'due'

  const followUpsResult = useLiveQuery((q) =>
    q.from({ followUps: db.followUpsCollection }),
  )
  const customersResult = useLiveQuery((q) =>
    q.from({ customers: db.customersCollection }),
  )
  const customerNotesResult = useLiveQuery((q) =>
    q.from({ notes: db.customerNotesCollection }),
  )
  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const categoriesResult = useLiveQuery((q) =>
    q.from({ categories: db.followUpCategoriesCollection }),
  )

  const categories = ((categoriesResult.data ?? []) as FollowUpCategory[])
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
  const categoriesById = new Map(categories.map((c) => [c.id, c]))

  // ---- Server paging for the Upcoming and Archive tabs ---------------------
  // The synced collection carries only the due slice; these tabs page through
  // db.searchFollowUps, which upserts each page into the collection. The
  // membership state owns which ids show and in what order (same pattern as
  // CustomersScreenContainer).
  const [assignedToMe, setAssignedToMe] = useState(false)
  const [membership, setMembership] = useState<{
    keys: string[]
    totalItems: number
  }>({ keys: [], totalItems: 0 })
  const [serverLoading, setServerLoading] = useState(false)
  const [serverError, setServerError] = useState(false)
  const [upcomingCount, setUpcomingCount] = useState<number | undefined>()

  const lastQueryRef = useRef<{
    scope: FollowUpsServerScope
    query: LedgerServerQuery
  } | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const requestSeqRef = useRef(0)
  const countSeqRef = useRef(0)

  // The ledger filters carry display names; resolving them to ids needs the
  // users/categories lists. A ref keeps runQuery's identity stable so the
  // table's query effect doesn't re-fire on every render.
  const filterContextRef = useRef({
    assignedToMe,
    userId: user?.id,
    users: (usersResult.data ?? []) as Array<{ id: string; name: string }>,
    categories,
  })
  filterContextRef.current = {
    assignedToMe,
    userId: user?.id,
    users: (usersResult.data ?? []) as Array<{ id: string; name: string }>,
    categories,
  }

  const toSearchFilters = (
    filters: Record<string, string>,
  ): FollowUpsSearchFilters => {
    const context = filterContextRef.current
    const assigneeName = filters.assignee?.trim()
    // "Assigned to me" scopes the whole page, so it wins over the column filter.
    const assigneeId =
      context.assignedToMe && context.userId
        ? context.userId
        : assigneeName
          ? (context.users.find((staff) => staff.name === assigneeName)?.id ??
            NO_MATCH_ID)
          : undefined
    const categoryName = filters.category?.trim()
    return {
      customer: filters.customer || undefined,
      title: filters.title || undefined,
      assigneeId,
      categoryId: categoryName
        ? (context.categories.find((c) => c.name === categoryName)?.id ??
          NO_MATCH_ID)
        : undefined,
    }
  }

  const runQuery = useCallback(
    async (scope: FollowUpsServerScope, query: LedgerServerQuery) => {
      const seq = ++requestSeqRef.current
      setServerLoading(true)
      try {
        const response = await db.searchFollowUps({
          scope,
          filters: toSearchFilters(query.filters),
          sort:
            query.sort && isSortKey(query.sort.key)
              ? { key: query.sort.key, dir: query.sort.dir }
              : undefined,
          page: query.page,
          pageSize: query.pageSize,
        })
        if (seq !== requestSeqRef.current) return
        setMembership({ keys: response.keys, totalItems: response.totalItems })
        setServerError(false)
      } catch (err) {
        if (seq !== requestSeqRef.current) return
        console.error('Follow-ups search failed', err)
        setServerError(true)
      } finally {
        if (seq === requestSeqRef.current) setServerLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [db],
  )

  const handleQueryChange = useCallback(
    (scope: FollowUpsServerScope, query: LedgerServerQuery) => {
      const previous = lastQueryRef.current
      lastQueryRef.current = { scope, query }
      if (debounceRef.current) clearTimeout(debounceRef.current)

      // Typing in a text filter debounces; sort, page and select changes fire
      // immediately.
      if (
        previous &&
        previous.scope === scope &&
        textFiltersChanged(previous.query.filters, query.filters)
      ) {
        debounceRef.current = setTimeout(() => runQuery(scope, query), 300)
      } else {
        runQuery(scope, query)
      }
    },
    [runQuery],
  )

  /** The Upcoming tab label's total, fetched as a zero-row search. */
  const refreshUpcomingCount = useCallback(async () => {
    const seq = ++countSeqRef.current
    try {
      const context = filterContextRef.current
      const response = await db.searchFollowUps({
        scope: 'upcoming',
        filters:
          context.assignedToMe && context.userId
            ? { assigneeId: context.userId }
            : {},
        page: 1,
        pageSize: 1,
      })
      if (seq === countSeqRef.current) setUpcomingCount(response.totalItems)
    } catch (err) {
      // The count is cosmetic — leave the label bare rather than error out.
      console.error('Upcoming follow-ups count failed', err)
    }
  }, [db])

  /** Re-runs the last search + count once a mutation has had time to sync. */
  const scheduleServerRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    refreshTimerRef.current = setTimeout(() => {
      if (lastQueryRef.current) {
        runQuery(lastQueryRef.current.scope, lastQueryRef.current.query)
      }
      refreshUpcomingCount()
    }, SERVER_REFRESH_DELAY_MS)
  }, [runQuery, refreshUpcomingCount])

  // The toggle changes what every server query means: re-run from page 1.
  useEffect(() => {
    refreshUpcomingCount()
    if (lastQueryRef.current) {
      const { scope, query } = lastQueryRef.current
      lastQueryRef.current = { scope, query: { ...query, page: 1 } }
      runQuery(scope, { ...query, page: 1 })
    }
  }, [assignedToMe, refreshUpcomingCount, runQuery])

  // Self-heal: a base-sync refetch can evict upserted rows the current page
  // still references. Missing records show up as keys with no row — re-query
  // (the search re-upserts them, or drops the keys if they're truly gone).
  const followUpIds = new Set(
    ((followUpsResult.data ?? []) as FollowUp[]).map((followUp) => followUp.id),
  )
  const hasMissingServerRow =
    activeTab !== 'due' &&
    membership.keys.some((key) => !followUpIds.has(key))
  useEffect(() => {
    if (hasMissingServerRow && !serverLoading) scheduleServerRefresh()
  }, [hasMissingServerRow, serverLoading, scheduleServerRefresh])

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    }
  }, [])

  const customers = (customersResult.data ?? []) as Customer[]
  const customersById = new Map(customers.map((customer) => [customer.id, customer]))

  // The notes that answered each follow-up, newest first — the readable half
  // of the conversation the reminder only points at.
  const notesByFollowUpId = new Map<string, FollowUpLinkedNote[]>()
  // The same notes as a feed for the side panel's thread — deleted ones stay
  // in as tombstones, so this grouping keeps them.
  const threadNotesByFollowUpId = new Map<string, CustomerNote[]>()
  for (const note of (customerNotesResult.data ?? []) as CustomerNote[]) {
    if (!note.followUpId) continue
    const thread = threadNotesByFollowUpId.get(note.followUpId) ?? []
    thread.push(note)
    threadNotesByFollowUpId.set(note.followUpId, thread)
    if (note.deletedAt) continue
    const linked = notesByFollowUpId.get(note.followUpId) ?? []
    linked.push({
      id: note.id,
      text: note.text ?? note.caption ?? '(photo note)',
      authorName: db.getAuthorDisplayName(note.authorId),
      createdAt: note.createdAt,
    })
    notesByFollowUpId.set(note.followUpId, linked)
  }
  for (const linked of notesByFollowUpId.values()) {
    linked.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }

  if (followUpsResult.isLoading || customersResult.isLoading) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-container mx-auto px-4 py-6 space-y-2">
          <h1 className="text-2xl font-semibold">Follow-ups</h1>
          <p className="text-sm text-slate-600">Loading follow-ups…</p>
        </main>
      </div>
    )
  }

  if (followUpsResult.isError || customersResult.isError) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-container mx-auto px-4 py-6 space-y-2">
          <h1 className="text-2xl font-semibold">Follow-ups</h1>
          <p className="text-sm text-slate-600">
            Unable to load follow-ups right now.
          </p>
        </main>
      </div>
    )
  }

  const rows: FollowUpRow[] = ((followUpsResult.data ?? []) as FollowUp[]).map(
    (followUp) => ({
      id: followUp.id,
      customerId: followUp.customerId,
      customerPublicId: customersById.get(followUp.customerId)?.publicId,
      customerName:
        customersById.get(followUp.customerId)?.fullName ?? 'Unknown customer',
      customerPhone: customersById.get(followUp.customerId)?.contactPhone,
      customerEmail: customersById.get(followUp.customerId)?.contactEmail,
      categoryId: followUp.categoryId,
      categoryName: followUp.categoryId
        ? categoriesById.get(followUp.categoryId)?.name
        : undefined,
      assigneeId: followUp.assigneeId,
      assigneeName: followUp.assigneeId
        ? db.getAuthorDisplayName(followUp.assigneeId)
        : undefined,
      createdByName: followUp.createdBy
        ? db.getAuthorDisplayName(followUp.createdBy)
        : undefined,
      createdAt: followUp.createdAt,
      title: followUp.title,
      details: followUp.details,
      dueAt: followUp.dueAt,
      schedule: followUp.schedule,
      scheduleIndex: followUp.scheduleIndex,
      status: followUp.status,
      service: followUp.service
        ? {
            ...followUp.service,
            label: serviceLabels[followUp.service.type],
          }
        : undefined,
      lastDoneAt: followUp.lastDoneAt,
      notes: notesByFollowUpId.get(followUp.id),
      feed: buildCustomerNoteFeed(
        threadNotesByFollowUpId.get(followUp.id),
        db.getAuthorDisplayName,
      ),
    }),
  )

  return (
    <FollowUpsScreen
      rows={rows}
      activeTab={activeTab}
      onSelectTab={(tab) =>
        router.navigate({
          to: '/follow-ups',
          search: tab === 'due' ? {} : { tab },
        })
      }
      startCreating={Boolean(search.new)}
      onCreateClosed={() =>
        router.navigate({
          to: '/follow-ups',
          search: activeTab === 'due' ? {} : { tab: activeTab },
          replace: true,
        })
      }
      assigneeOptions={(usersResult.data ?? []).map((staff) => ({
        id: staff.id,
        name: staff.name,
      }))}
      categoryOptions={categories.map((c) => ({ id: c.id, name: c.name }))}
      currentUserId={user?.id}
      serverTab={{
        keys: membership.keys,
        totalItems: membership.totalItems,
        loading: serverLoading,
        error: serverError,
        onQueryChange: handleQueryChange,
      }}
      upcomingCount={upcomingCount}
      assignedToMe={assignedToMe}
      onToggleAssignedToMe={() => setAssignedToMe((on) => !on)}
      onCreateCustomer={(input) =>
        // Invoice details mirror the contact ones until someone fills them in
        // properly on the customer page. Awaited: follow_ups.customer_id is a
        // relation, so the customer must exist server-side first.
        db.createCustomerAndWait({
          fullName: input.fullName,
          contactPhone: input.contactPhone ?? '',
          contactEmail: input.contactEmail ?? '',
          invoiceName: input.fullName,
          invoiceAddress: '',
          invoiceEmail: input.contactEmail ?? '',
          invoicePhone: input.contactPhone ?? '',
        })
      }
      onCreate={(input, extras) => {
        const created = db.createFollowUp(input)
        // The opening note lands on the customer thread tagged with the new
        // follow-up, same as notes added later from the side panel.
        if (extras.firstNote) {
          db.appendCustomerNote(input.customerId, {
            text: extras.firstNote,
            followUpId: created.id,
          })
        }
        scheduleServerRefresh()
      }}
      onEdit={(row, input) => {
        const patch: {
          title?: string
          schedule?: ScheduleStep[]
          categoryId?: string
        } = {}
        if (input.title !== row.title) patch.title = input.title
        if ((input.categoryId ?? '') !== (row.categoryId ?? '')) {
          patch.categoryId = input.categoryId ?? ''
        }
        // The dialog edited the *remaining* ladder — only a real change
        // replaces the schedule (which also resets its index).
        if (
          JSON.stringify(input.schedule ?? []) !==
          JSON.stringify(remainingSchedule(row))
        ) {
          patch.schedule = input.schedule ?? []
        }
        if (Object.keys(patch).length > 0) {
          db.updateFollowUpDetails(row.id, patch)
        }
        // Day-granular compare: the dialog re-emits the same day at 08:00,
        // which must not record a phantom reschedule.
        if (toZaDateKey(input.dueAt) !== toZaDateKey(row.dueAt)) {
          db.rescheduleFollowUp(row.id, input.dueAt, {})
        }
        if ((input.assigneeId ?? '') !== (row.assigneeId ?? '')) {
          db.assignFollowUp(row.id, input.assigneeId ?? '')
        }
        scheduleServerRefresh()
      }}
      onComplete={(id, options) => {
        db.completeFollowUp(id, options)
        scheduleServerRefresh()
      }}
      onSnooze={(id, step, options) => {
        db.snoozeFollowUp(id, step, options)
        scheduleServerRefresh()
      }}
      onReschedule={(id, dueAt, options) => {
        db.rescheduleFollowUp(id, dueAt, options)
        scheduleServerRefresh()
      }}
      onClose={(id, options) => {
        db.closeFollowUp(id, options)
        scheduleServerRefresh()
      }}
      onReopen={(id, dueAt, options) => {
        db.reopenFollowUp(id, dueAt, options)
        scheduleServerRefresh()
      }}
      onAssign={(id, userId) => {
        db.assignFollowUp(id, userId)
        scheduleServerRefresh()
      }}
      onAddNote={(row, text) => {
        if (!text.trim()) return false
        db.appendCustomerNote(row.customerId, { text, followUpId: row.id })
        return true
      }}
      onSubmitPhotoNote={(row, input) => {
        setPhotoNoteError(null)
        const result = db.appendCustomerNoteWithTransaction(row.customerId, {
          caption: input.caption.trim(),
          photoUrl: input.photoUrls[0],
          photoUrls: input.photoUrls,
          photoFiles: input.photoFiles,
          followUpId: row.id,
        })

        result.transaction?.isPersisted?.promise
          ?.then(() => setPhotoNoteError(null))
          .catch((error: unknown) => {
            setPhotoNoteError(
              formatPocketBaseError(error, {
                fallbackMessage: 'Unable to save photo note. Please try again.',
                fieldLabels: { photo: 'Photo' },
              }),
            )
            try {
              // Hard delete: the upload failed, so this note never joined the
              // thread and should leave no tombstone.
              if (db.customerNotesCollection.has?.(result.note.id)) {
                db.discardCustomerNote(result.note.id)
              }
            } catch {}
          })
      }}
      onReplacePhotoNote={(row, id, input) =>
        db.updateCustomerNote(row.customerId, id, {
          photoUrl: input.photoUrl,
          photoFile: input.photoFile,
        })
      }
      onDeleteNote={(row, id) => db.deleteCustomerNote(row.customerId, id)}
      onSaveEditNote={(row, id, next) => {
        // The card hands back one body string; whether that is the text or
        // the caption depends on what kind of note it is.
        const note = db.customerNotesCollection.get?.(id)
        const isPhoto = Boolean(note?.photoUrl || note?.photo)
        db.updateCustomerNote(
          row.customerId,
          id,
          isPhoto ? { caption: next } : { text: next },
        )
      }}
      photoNoteError={photoNoteError}
      onClearPhotoNoteError={() => setPhotoNoteError(null)}
      // The thread is where the follow-up's story lives, so opening a row
      // deep-links straight into that thread on the customer's notes tab.
      onOpenCustomer={(row) => {
        const customer = customersById.get(row.customerId)
        router.navigate({
          to: '/customers/$id/notes',
          params: { id: customer?.publicId ?? row.customerId },
          search: { thread: `fu:${row.id}` },
        })
      }}
    />
  )
}
